import { DataTable, DataTableExpandedRows, DataTableStateEvent } from "primereact/datatable";
import { FilterMatchMode } from "primereact/api";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./FleetTable.css";
import { Tooltip } from "primereact/tooltip";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { registerCustomFilters } from "./FilterTemplates";
import { actionBodyTemplate, rowExpansionTemplate } from "../Shared/BodyTemplates";
import LOCAL_TABLE_CONFIG_KEY from "../../types/TableLocalConfigKeys";
import { FleetTableFields } from "./FleetTableFields";
import {
    buildColumns,
    loadSavedColumnConfigurationsV2,
    updateStoredTableConfigState
} from "../Shared/TableFunctions";
import { SharedTableColumnConfig } from "../../types/SharedTableColumnConfig";
import {
    columnTemplates,
    DEFAULT_FLEET_TABLE_PAGE_SIZE,
    filterTemplates,
    keywordSearchFilterFields
} from "./Constants";
import { getDefaultFleetColumnsConfig } from "./Columns/ColumnConfigs";
import { getCustomColumns } from "./Columns/ColumnHelper";
import { SelectItemOptionsType } from "primereact/selectitem";
import { USER_APP_STATE_HOME_TABLE_KEY } from "../../utils/Constants";
import { actionColumn, vesselColumn } from "./Columns/StaticColumns";
import { onCustomRestoreState } from "../../utils/TableUtils";
import { vesselBodyTemplate } from "@idwal/idwal-react-components";
import { useAppDispatch, useAppSelector } from "../../hooks/storeHooks";
import { selectUserDetails } from "../../store/selectors/userSelectors";
import {
    selectDataTableFilterData,
    selectKeywordSearch
} from "../../store/selectors/filtersSelectors";
import { setFleetTableFilterData } from "../../store/slices/filtersSlice";
import { selectVesselByImo } from "../../store/thunks/vesselViewThunks";
import { selectFleet } from "../../store/selectors/fleetSelectors";
import {
    setRequestInspectionDialog,
    updateFleetDataItem
} from "../../store/slices/fleetSlice";
import { cloneDeep } from "lodash";
import { FleetDataItem } from "../../types/FleetDataItem";
import { updateGroupData } from "../../store/thunks/groupThunks";
import { FleetTableHeader } from "./FleetTableHeader";
import { Column } from "primereact/column";

export const FleetTable = () => {
    const { t } = useTranslation("locale");
    const dispatch = useAppDispatch();

    const fleetTableRef = useRef<any>(null);

    const {
        loading,
        fleetDataItems,
        distinctVesselTypes,
        distinctTechnicalManagers,
        distinctCountriesOfBuild,
        distinctFlags
    } = useAppSelector(selectFleet);

    const userDetails = useAppSelector(selectUserDetails);
    const { table: keywordSearch } = useAppSelector(selectKeywordSearch);
    const { table: filters } = useAppSelector(selectDataTableFilterData);
    const [searchParams] = useSearchParams();

    registerCustomFilters();

    const updateRowItem = (item: FleetDataItem) => {
        dispatch(updateFleetDataItem(item));
    };

    const onComplete = (data: Record<string, unknown>) => {
        dispatch(updateGroupData(data));
    };

    const defaultColumns = useMemo(
        () => getDefaultFleetColumnsConfig(updateRowItem, onComplete),
        [updateRowItem, onComplete]
    );

    const [columns, setColumns] =
        useState<SharedTableColumnConfig[]>(defaultColumns);
    const [selectedColumns, setSelectedColumns] = useState(columns);
    const [first, setFirst] = useState(0);
    const [rows, setRows] = useState(DEFAULT_FLEET_TABLE_PAGE_SIZE);
    const [expandedRows, setExpandedRows] = useState<DataTableExpandedRows | undefined>(undefined);

    const rowOptions = useMemo(() => [5, 10, 25, 50], []);

    const tableDataItems = useMemo(
        () => cloneDeep(fleetDataItems),
        [fleetDataItems]
    );

    const onPage = useCallback((event: any) => {
        setFirst(event.first);
        setRows(event.rows);
    }, []);

    useEffect(() => {
        if (loading || !userDetails?.groupDetails || !tableDataItems.length) {
            return;
        }

        const customColumns = getCustomColumns(
            userDetails,
            columns,
            tableDataItems,
            onComplete
        );
        
        const joinedColumns = columns.concat(customColumns);

        loadSavedColumnConfigurationsV2(
            joinedColumns,
            setColumns,
            setSelectedColumns,
            userDetails,
            userDetails?.appState?.homeTable,
            USER_APP_STATE_HOME_TABLE_KEY
        );
    }, [loading, tableDataItems]);

    useEffect(() => {
        if (!loading) {
            const inspectionStatusParam = searchParams.get("inspectionStatus");
            
            if (inspectionStatusParam) {
                const statusValues = inspectionStatusParam.split(',');
                
                // Use a timeout to ensure this runs after other filter initialization
                setTimeout(() => {
                    const currentFilters = { ...filters };
                    currentFilters[FleetTableFields.VESSEL_INSPECTION_STATUS] = {
                        value: statusValues,
                        matchMode: FilterMatchMode.CUSTOM
                    };
                    
                    dispatch(setFleetTableFilterData(currentFilters));
                }, 100);
            }
        }
    }, [searchParams, loading]);

    const globalFilterFields = useMemo(
        () =>
            keywordSearchFilterFields(),
        []
    );
    const vesselColumns = useMemo(
        () => vesselColumn((e: any) => vesselBodyTemplate(e, selectVessel)),
        []
    );
    const actionColumns = useMemo(
        () =>
            actionColumn((e: any) =>
                actionBodyTemplate(e, requestInspectionShow)
            ),
        []
    );

    const inspectionStatusFilterOptions: SelectItemOptionsType[] = useMemo(
        () => [
            t("inspectionStatuses.ordered"),
            t("inspectionStatuses.committed"),
            t("inspectionStatuses.inProgress"),
            t("inspectionStatuses.inspectionComplete"),
            t("inspectionStatuses.fullReport"),
            t("inspectionStatuses.neverInspected")
        ],
        []
    );

    const onFilter = useCallback((event: DataTableStateEvent) => {
        dispatch(setFleetTableFilterData(event.filters));
    }, []);

    const selectVessel = useCallback((rowData: any) => {
        dispatch(selectVesselByImo(rowData.vessel.imo));
    }, []);

    const header = useMemo(
        () => (
            <FleetTableHeader
                columns={columns}
                setColumns={setColumns}
                selectedColumns={selectedColumns}
                setSelectedColumns={setSelectedColumns}
            />
        ),
        [columns, selectedColumns]
    );

    const requestInspectionShow = useCallback((rowData: any) => {
        dispatch(
            setRequestInspectionDialog({
                show: true,
                vessels: [rowData]
            })
        );
    }, []);

    const columnComponents = useMemo(
        () =>
            buildColumns(
                selectedColumns,
                columnTemplates(
                    requestInspectionShow
                ),
                filterTemplates(
                    distinctVesselTypes,
                    distinctTechnicalManagers,
                    distinctFlags,
                    distinctCountriesOfBuild,
                    inspectionStatusFilterOptions
                ),
                () => "12rem"
            ),
        [
            selectedColumns,
            distinctVesselTypes,
            distinctTechnicalManagers,
            distinctFlags,
            distinctCountriesOfBuild,
            inspectionStatusFilterOptions
        ]
    );

    const customSaveState = useCallback(
        (state: any) => {
            updateStoredTableConfigState(state, LOCAL_TABLE_CONFIG_KEY.FLEET);
        },
        [columns, selectedColumns, userDetails]
    );

    const customRestoreState = useCallback(
        () =>
            onCustomRestoreState(
                LOCAL_TABLE_CONFIG_KEY.FLEET,
                keywordSearch),
        [userDetails, keywordSearch]
    );

    if (loading) {
        return (
            <div
                className={"table-container home-table"}
                data-cy="fleet-table-container-loading"
            >
                <DataTable
                    loading
                    header={header}
                    filters={filters}
                    filterDisplay="row"
                    data-cy="fleet-table-loading"
                >
                    {vesselColumns}
                </DataTable>
            </div>
        );
    }

    return (
        <div
            className={"table-container home-table"}
            data-cy={"fleet-table-container"}
        >
            <Tooltip
                target=".request-inspection"
                content={t("fleetTable.requestInspection")}
                position="left"
            />
            <DataTable
                ref={fleetTableRef}
                globalFilterFields={globalFilterFields}
                paginator
                first={first ?? 0}
                onPage={onPage}
                rowsPerPageOptions={rowOptions}
                rows={rows ?? DEFAULT_FLEET_TABLE_PAGE_SIZE}
                scrollable
                data-cy="fleet-table"
                filters={filters}
                filterDisplay="row"
                header={header}
                value={tableDataItems}
                onFilter={onFilter}
                stateStorage="custom"
                customSaveState={customSaveState}
                customRestoreState={customRestoreState}
                loading={loading}
                emptyMessage={t("fleetTable.noDataMessage")}
                removableSort
                editMode="cell"
                stripedRows
                resizableColumns
                columnResizeMode="expand"
                expandedRows={expandedRows}
                onRowToggle={(e) => setExpandedRows(e.data as DataTableExpandedRows)}
                rowExpansionTemplate={rowExpansionTemplate}
            >
                {<Column style={{width: "3em"}} expander frozen />}
                {vesselColumns}
                {actionColumns}
                {columnComponents}
            </DataTable>
        </div>
    );
};
